import React, { useState } from "react";
import { ArrowRight, Check, Sparkles, Building2, House, Store, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  id: "residential",
  title: "Panel Replacements",
  icon: House,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/electrical_panel_upgrade.png",
  description: "Modernize your infrastructure with high-capacity panel upgrades designed for safety and future growth.",
  features: ["200 Amp Upgrades", "Code Compliance", "Safety Testing", "Surge Protection"],
  link: "/ResidentialPage"
}, {
  id: "commercial",
  title: "Wiring Inspections",
  icon: Search,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/wiring_inspection_tech.png",
  description: "Comprehensive diagnostic evaluations to ensure your facility's electrical integrity and operational safety.",
  features: ["Thermal Imaging", "Load Analysis", "Grounding Checks", "Detailed Reporting"],
  link: "/CommercialPage"
}, {
  id: "storefront",
  title: "Commercial Projects",
  icon: Building2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/commercial_construction_site.png",
  description: "Large-scale electrical contracting for new construction, tenant build-outs, and industrial facilities.",
  features: ["Three-Phase Power", "LED Retrofitting", "Project Management", "Certified Quality"],
  link: "/CommercialPage"
}, {
  id: "details",
  title: "24/7 Service Calls",
  icon: Store,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/emergency_electrical_repair.png",
  description: "Immediate response for electrical emergencies. Our master technicians are available around the clock.",
  features: ["Rapid Response", "Troubleshooting", "Fault Detection", "Storm Repairs"],
  link: "/ContactPage"
}];
export default function CoreCleaningSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeId, setActiveId] = useState<string | null>(null);
  return <section data-section-id="60850" id="business-electrical-solutions" ref={ref} className={cn("relative w-full py-24 overflow-hidden bg-background transition-opacity duration-700", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center p-2 bg-muted rounded-sm mb-4 backdrop-blur-sm border border-border">
            <Sparkles className="w-4 h-4 text-primary mr-2" />
            <span className="text-sm font-mono font-medium text-secondary uppercase tracking-widest">
              Industrial Expertise
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tighter text-foreground mb-4">
            Enterprise Electrical Power
          </h2>
          <p className="text-xl text-muted-foreground">
            Precision engineering for Richmond's commercial infrastructure. Explore our specialized solutions 
            designed to keep your business powered and protected.
          </p>
        </div>

        {}
        <div className="flex flex-col lg:flex-row gap-4 h-auto lg:h-[600px] w-full">
          {services.map((service, index) => {
          const isActive = activeId === service.id;
          const isAnyActive = activeId !== null;
          return <div key={service.id} data-index={index} onMouseEnter={() => setActiveId(service.id)} onMouseLeave={() => setActiveId(null)} className={cn("group relative flex flex-col overflow-hidden rounded-sm transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)] cursor-pointer border border-border shadow-md", "h-[400px] w-full lg:h-full", "lg:flex-1", isActive ? "lg:grow-[2.5]" : "lg:grow-1", isAnyActive && !isActive ? "opacity-60 grayscale" : "opacity-100 grayscale-0")}>
                {}
                <div className={cn("absolute inset-0 bg-cover bg-center transition-all duration-700", isActive ? "scale-110 grayscale-0" : "scale-100 grayscale")} style={{
              backgroundImage: `url(${service.image})`
            }} />

                {}
                <div className={cn("absolute inset-0 bg-gradient-to-t from-secondary/95 via-secondary/50 to-transparent transition-opacity duration-500", isActive ? "opacity-95" : "opacity-80")} />

                {}
                <div className="relative h-full flex flex-col justify-end p-6 md:p-8 z-10 text-white">
                  {}
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-sm bg-white/10 backdrop-blur-md border border-white/20 text-white">
                      <service.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-2xl font-bold tracking-tight text-white uppercase">
                      {service.title}
                    </h3>
                  </div>

                  {}
                  <div className={cn("overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.25,1,0.5,1)]", isActive ? "max-h-[400px] opacity-100 mt-4" : "max-h-0 opacity-0 mt-0 lg:max-h-0 lg:opacity-0")}>
                    <p className="text-white/90 mb-6 leading-relaxed font-sans">
                      {service.description}
                    </p>

                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-8">
                      {service.features.map((feature, i) => <li key={i} className="flex items-center text-sm font-mono text-white/80">
                          <Check className="w-4 h-4 mr-2 text-primary" />
                          {feature}
                        </li>)}
                    </ul>

                    <div className="flex flex-wrap gap-3">
                      <Button asChild variant="default" className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-sm border-none font-bold uppercase tracking-wide">
                        <Link to="/ContactPage">
                          Get Quote
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white rounded-sm font-bold uppercase tracking-wide">
                        
                      </Button>
                    </div>
                  </div>
                  
                  {}
                  <div className={cn("lg:hidden text-xs font-mono text-white/60 mt-2 flex items-center gap-1", isActive ? "hidden" : "block")}>
                    EXPAND DETAILS <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}